import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { userLoginRequest } from '../Types/UsersType';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { getEmailValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import { loginUser, reactivateAccount } from '../Services/UserService';
import { PasswordInput } from '../../Shared/Component/forms/PasswordInput';
import { MyTokenPayload } from '../../Shared/Services/JwtToken';
import { jwtDecode } from 'jwt-decode';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { storage } from '../../Shared/Utils/authStorage';
import { fetchCurrentPrivileges } from '../../Admin/Services/AdminRoleService';

function LoginComponent() {
  const {
    values,
    handleInputChange,
  } = useFormController<userLoginRequest>({
    email: "",
    password: ""
  });

  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [loading, setLoading] = useState(false); // Added for UX
  // Set when sign-in is refused because the account is deactivated, so the form can
  // offer the one action that actually gets the user back in.
  const [showReactivate, setShowReactivate] = useState(false);
  const redirect = useRedirect();
  const { policy } = useValidationPolicy();

  const DEACTIVATED_HINT = 'deactivated';

  /** Shared tail of a successful login and a successful reactivation. */
  const completeSession = async (authData: any) => {
    storage.setToken(authData.accessToken);

    storage.setUser({
      id: authData.userId,
      role: authData.role
    });

    if (authData.role === 'SUPER_ADMIN' || authData.role === 'DELEGATED_ADMIN') {
      try {
        const privilegesRes = await fetchCurrentPrivileges();
        storage.setPrivileges(privilegesRes.success && privilegesRes.data ? privilegesRes.data : []);
      } catch {
        storage.setPrivileges([]);
      }
    }

    window.dispatchEvent(
      new Event("auth-login")
    );

    const systemRole = authData.role;

    if (systemRole === "RECRUITER") {
      redirect("/recruiter/dashboard");
    } else if (systemRole === "CANDIDATE") {
      redirect("/candidate/dashboard");
    } else if (systemRole === "SUPER_ADMIN") {
      redirect("/admin/dashboard");
    } else if (systemRole === "DELEGATED_ADMIN") {
      // A delegate only has the read-only data service surface.
      redirect("/data-service");
    } else {
      redirect("/");
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    setAlert(null);

    const response = await reactivateAccount(values);

    if (response.success) {
      await completeSession(response.data);
      return;
    }

    setAlert({
      variant: "error",
      title: "Reactivation Error",
      message: response.message,
      autoCloseMs: 5000,
      onClose: () => setAlert(null),
    });

    setLoading(false);
  };



  const handleSubmit = async () => {
    const emailError = getEmailValidationError(values.email, policy);
    if (emailError) {
      setAlert({
        variant: "error",
        title: "Invalid Email",
        message: emailError,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    setLoading(true);
    setAlert(null);
  
    const response = await loginUser(values);
  
    if (
      response.success &&
      response.message.toLowerCase().includes("success")
    ) {
      await completeSession(response.data);
      return;
    }

    // The only reason sign-in ever names deactivation is that the account was switched
    // off. Surface the way back in rather than leaving the user at a dead end.
    const deactivated =
      typeof response.message === 'string' &&
      response.message.toLowerCase().includes(DEACTIVATED_HINT);
    setShowReactivate(deactivated);

    setAlert({
      variant: response.status ? "success" : "error",
      title: response.success ? "Success...!" : "Login Error",
      message: response.message,
      autoCloseMs: deactivated ? 0 : 5000,
      onClose: () => setAlert(null),
    });
  
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Alert Section */}
      <div className="mb-4">
            {alert && (
              <Alert 
                variant={alert.variant} 
                title={alert.title} 
                message={alert.message} 
                onClose={() => setAlert(null)} 
              />
            )}
          </div>
      {/* Decorative colorful background glows for that vibrant cyberpunk feel */}
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
        {/* Logo or Header Icon could go here */}
        <h2 className="mt-6 text-center text-3xl font-extrabold bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-slate-400 font-medium tracking-wide">
          Welcome back! Please enter your details.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="bg-slate-900/80 backdrop-blur-xl py-8 px-4 shadow-2xl shadow-cyan-950/20 border border-slate-800 rounded-xl sm:px-10 relative before:absolute before:inset-0 before:p-[1px] before:bg-gradient-to-b before:from-slate-700 before:to-transparent before:rounded-xl before:-z-10">
          
          

          <div className="space-y-6">
            <div className="group">
              <TextInput
                label="Email Address"
                name="email"
                type="email"
                placeholder="name@company.com"
                value={values.email}
                onChange={handleInputChange("email")}
                // text-slate-200 ensures labels/text within the component switch to white/light-gray
                className="text-slate-200 focus:border-cyan-400 focus:ring-cyan-400/20" 
              />
            </div>

            <div >
              <PasswordInput
                label="Password"
                name="password"
                placeholder="••••••••"
                value={values.password}
                onChange={handleInputChange("password")}
                // text-slate-200 ensures labels/text within the component switch to white/light-gray
                className="text-slate-200 focus:border-cyan-400 focus:ring-cyan-400/20"
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-cyan-400 hover:text-cyan-300 font-medium tracking-wide"
              >
                Forgot password?
              </Link>
            </div>

            <div>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-lg shadow-cyan-500/20 text-sm font-semibold text-slate-950 bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400 hover:from-cyan-300 hover:via-indigo-300 hover:to-fuchsia-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-cyan-400 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Signing in..." : "Login"}
              </button>
            </div>

            {showReactivate && !loading && (
              <div>
                <button
                  onClick={handleReactivate}
                  disabled={loading}
                  className="w-full flex justify-center py-2.5 px-4 border border-cyan-400/40 rounded-md text-sm font-semibold text-cyan-300 bg-cyan-400/5 hover:bg-cyan-400/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-cyan-400 transition-all duration-300"
                >
                  Reactivate my account
                </button>
                <p className="mt-2 text-center text-xs text-slate-500">
                  Uses the email and password above. Not available if a deletion request is
                  still awaiting review.
                </p>
              </div>
            )}
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-slate-900 text-cyan-400/80 font-mono tracking-wider text-xs uppercase">
                  Secure Encrypted Login
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginComponent;